import { useState, useCallback } from 'react';

/**
 * useState с автосохранением в sessionStorage.
 *
 * Восстанавливает значение при ремонтировании компонента (например, при навигации назад).
 * Состояние живёт до закрытия вкладки браузера.
 *
 * @param {string} key — ключ в sessionStorage
 * @param {*} defaultValue — значение по умолчанию
 * @param {Object} options
 * @param {Function} options.serialize — кастомная сериализация (например, Set → Array)
 * @param {Function} options.deserialize — кастомная десериализация (например, Array → Set)
 * @returns {[*, Function]} — [state, setState] идентичный API useState
 *
 * Пример:
 *   const [page, setPage] = useSessionState('orgs:page', 1);
 *   const [filters, setFilters] = useSessionState('orgs:filters', defaultFilters, {
 *     serialize: (filters) => mapValues(filters, set => [...set]),
 *     deserialize: (raw) => mapValues(raw, arr => new Set(arr)),
 *   });
 */
export function useSessionState(key, defaultValue, options = {}) {
  const { serialize, deserialize } = options;

  const [state, setStateRaw] = useState(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        return deserialize ? deserialize(parsed) : parsed;
      }
    } catch (e) {
      // sessionStorage недоступен или данные повреждены — используем default
    }
    return defaultValue;
  });

  const setState = useCallback((valueOrFn) => {
    setStateRaw((prev) => {
      const next = typeof valueOrFn === 'function' ? valueOrFn(prev) : valueOrFn;
      try {
        const toStore = serialize ? serialize(next) : next;
        sessionStorage.setItem(key, JSON.stringify(toStore));
      } catch (e) {
        // sessionStorage недоступен или переполнен — state работает без персистенции
      }
      return next;
    });
  }, [key, serialize]);

  return [state, setState];
}
